import {createCanvasTimelineDate, GROUP_TYPE} from '../canvas-timeline/canvas_timeline_util';
import Util from '../../../forecast-app/shared/util/util';

const getTaskGroupsRecursive = (groups, taskGroups = []) => {
	for (let i = 0; i < groups.length; i++) {
		const group = groups[i];
		if (group.groupType === GROUP_TYPE.PROJECT_SCHEDULING_TASK) {
			taskGroups.push(group);
		} else if (group.groups && group.groups.length) {
			getTaskGroupsRecursive(group.groups, taskGroups);
		}
	}
	return taskGroups;
};

const mapToProjectGraphQLType = (pageComponent, group) => {
	const projectId = group.data.id;

	const stateData = pageComponent.getFilterData();

	const projectPersonsOnProject = stateData.projectPersonsByProject[projectId] || [];
	const assignedPersons = projectPersonsOnProject.map(pp => ({id: pp.personId}));
	const currentProjectStatus = stateData.projectStatusMap.get(projectId);
	const projectPersons = {edges: projectPersonsOnProject.map(pp => ({node: {isContactPerson: pp.isContactPerson, person: {id: pp.personId}}}))};
	const projectLabelsOnProject = stateData.projectLabelsByProject[projectId] || [];
	const projectLabels = {edges: projectLabelsOnProject.map(pl => ({node: {label: {id: pl.labelId}}}))};

	return {
		project: {...group.data, client: {id: group.data.clientId}},
		budgetType: group.data.budgetType,
		assignedPersons,
		currentProjectStatus,
		projectPersons,
		projectLabels,
	};
};

const mapToPersonGraphQLType = (pageComponent, group, startDate, pixelsPerDay, canvasWidth) => {
	const stateData = pageComponent.getFilterData();

	const personLabelsForPerson = stateData.personLabelsByPerson[group.data.personId] || [];
	const personLabels = personLabelsForPerson.map(l => ({label: {id: l.labelId}}));

	const personSkillsForPerson = stateData.personSkillsByPersonMap[group.data.personId] || [];
	const personSkills = personSkillsForPerson.map(ps => ({skill: {...ps}}));

	const endDate = startDate + canvasWidth / pixelsPerDay;
	const allocationsForPerson = stateData.allocationsByPerson[group.data.personId] || [];
	const allocatedToIds = allocationsForPerson
		.filter(allocation => {
			const allocationStartDate = createCanvasTimelineDate(allocation.startYear, allocation.startMonth, allocation.startDay);
			const allocationEndDate = createCanvasTimelineDate(allocation.endYear, allocation.endMonth, allocation.endDay);
			return allocationStartDate <= endDate && allocationEndDate >= startDate;
		})
		.map(allocation => {
			const {projectId, projectGroupId, idleTimeId} = allocation;
			// return id based on the allocation type. It could be project, projectgroup or idle time
			return projectId || projectGroupId || idleTimeId;
		});

	const departmentId = group.data.departmentId ? btoa(`DepartmentType:${group.data.departmentId}`) : undefined;

	return {
		...group.data,
		departmentId,
		id: group.data.personId,
		role: {id: group.data.roleId},
		personLabels: {edges: personLabels},
		personSkills,
		allocatedToIds,
	};
};

const mapToTaskGraphQLType = (pageComponent, group) => {
	const {task} = group.data;

	let taskGraphQlType = task.cachedFilterData;
	if (!taskGraphQlType) {
		const stateData = pageComponent.getFilterData();

		const statusColumn = stateData.statusColumnMap.get(task.statusColumnId);
		const assignedPersons = task.assignedPersons.map(assignedId => stateData.personMap.get(assignedId));
		const owner = task.ownerId ? stateData.personMap.get(task.ownerId) : null;
		const followers = task.followers ? task.followers.map(followerId => stateData.personMap.get(followerId)) : [];
		const taskLabelsForTask = task.taskLabels || [];
		const taskLabels = taskLabelsForTask.map(l => ({label: {id: l.labelId}}));
		const project = stateData.projectMap.get(task.projectId);

		taskGraphQlType = {
			blocked: task.blocked,
			bug: task.bug,
			highPriority: task.highPriority,
			billable: task.billable,
			favoured: task.favoured,
			project,
			phase: {
				id: task.phaseId,
			},
			role: {
				id: task.roleId,
			},
			statusColumnV2: {
				category: statusColumn && statusColumn.category ? statusColumn.category : null,
			},
			owner,
			followers,
			latestUiUpdateAt: task.latestUiUpdateAt,
			taskLabels,
			assignedPersons
		};
		task.cachedFilterData = taskGraphQlType;
	}

	return taskGraphQlType;
};

// FILTER V4
export const filterProjectGroupsV4 = (pageComponent, groups) => {
	const {searchFilterValue} = pageComponent.state;

	const visibleBySearch = (group, projectGroupGroup) => {
		let visibleBySearch = true;
		if (searchFilterValue && searchFilterValue.length) {
			const companyProjectId = `P-${group.data.companyProjectId}P${group.data.companyProjectId}`;
			const projectName = `${group.data.name}`;
			const projectClientName = `${group.data.clientName}`;
			const projectStatusName = `${group.data.status}`;
			const projectValues = `${companyProjectId}${projectName}${projectClientName}${projectStatusName}`;

			const companyProjectGroupId = projectGroupGroup ? `X-${projectGroupGroup.data.companyProjectGroupId}X${projectGroupGroup.data.companyProjectGroupId}` : '';
			const projectGroupName = projectGroupGroup ? `${projectGroupGroup.data.name}` : '';
			const projectGroupValues = `${companyProjectGroupId}${projectGroupName}`;

			const searchString = `${projectValues}${projectGroupValues}`;

			visibleBySearch = Util.normalizedIncludes(searchString, searchFilterValue);
		}
		return visibleBySearch;
	};

	if (pageComponent.state.filterFunctions) {
		for (const group of groups) {
			if (group.data.isConnectedProject) {
				let allProjectsFiltered = true;
				for (const childGroup of group.groups) {
					if (childGroup.groupType === GROUP_TYPE.PROJECT_SCHEDULING_PROJECT_SUB_GROUP) continue;

					const childProjectFilterData = mapToProjectGraphQLType(pageComponent, childGroup);

					childGroup.filtered = !pageComponent.state.filterFunctions.projectFilter(childProjectFilterData) || !visibleBySearch(childGroup, group);

					if (!childGroup.filtered) {
						allProjectsFiltered = false;
					}

					group.filtered = allProjectsFiltered;
				}
			} else {
				const projectFilterData = mapToProjectGraphQLType(pageComponent, group);

				group.filtered = !pageComponent.state.filterFunctions.projectFilter(projectFilterData) || !visibleBySearch(group);
			}
		}
	}
};

export const filterPersonsV4 = (pageComponent, group, _stepDataArray, startDate, pixelsPerDay, _timelineMinorStep, canvasWidth) => {
	if (pageComponent.state.filterFunctions) {
		const personFilterData = mapToPersonGraphQLType(pageComponent, group, startDate, pixelsPerDay, canvasWidth);

		const filterData = pageComponent.getFilterData();

		const options = {
			teams: filterData.relayTeams,
		};
		return !pageComponent.state.filterFunctions.personFilter(personFilterData, options);
	}
};

const doTaskFilter = (pageComponent, group) => {
	const taskGraphQLType = mapToTaskGraphQLType(pageComponent, group);
	const taskMatchesFilter = pageComponent.state.filterFunctions.taskFilter(taskGraphQLType)
	let subtaskMatchesFilter = false;
	group.groups.forEach(child => {
		if (doTaskFilter(pageComponent, child)) {
			subtaskMatchesFilter = true
		}
	});
	const matchesFilter = taskMatchesFilter || subtaskMatchesFilter;
	group.filtered = !matchesFilter;
	return matchesFilter;
};

export const filterTasksV4 = (pageComponent, groups) => {
	if (pageComponent.state.filterFunctions) {
		const taskGroups = getTaskGroupsRecursive(groups);
		for (const group of taskGroups) {
			doTaskFilter(pageComponent, group);
		}
	}
};
